const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// ─── Catalog types ────────────────────────────────────────────────────────────

export interface ProductForGroup {
  product_id: number;
  garment_id: number;
  color_id: number;
  size_id: number;
  garment_name: string;
  garment_base_price: number | string;
  color_name: string;
  color_hex: string;
  color_base_price: number | string;
  size_name: string;
  size_base_price: number | string;
  is_available: boolean;
  stock_available?: number;
  reserved_by_pending_stock_orders?: number | null;
  front_image_url: string | null;
  back_image_url: string | null;
  side_image_url: string | null;
  detail_image_url: string | null;
}

export interface GroupCustomization {
  option_id: number;
  price: number | string;
  custom_value: string | null;
  is_locked: boolean;
  is_mandatory: boolean;
  option: {
    id: number;
    name: string;
    value_type: string;
    possible_values?: any;
    is_custom: boolean;
    description?: string;
  };
}

export interface CatalogGroupInfo {
  id: string;
  name: string;
  code: string;
  delivery_option?: 'individual' | 'hybrid' | 'grouped';
  individual_option_price?: number | string | null;
  grouped_option_price?: number | string | null;
  delivery_addresses?: any[];
}

export interface CatalogResult {
  group: CatalogGroupInfo;
  products: ProductForGroup[];
  customizations: GroupCustomization[];
}

// ─── Calculate-price types ────────────────────────────────────────────────────

export interface CalculatePriceRequest {
  product_id: number;
  customizations?: { option_id: number; value: string }[];
  previous_order_id?: string | null;
  discount_codes?: string[];
  delivery_choice?: 'individual' | 'grouped';
}

export interface PriceBreakdownResult {
  garment_price: number;
  color_price: number;
  size_price: number;
  customizations_price: number;
  locked_customizations_price: number;
  subtotal: number;
  delivery_price?: number;
  discount: number;
  total: number;
}

export interface OrderChangeResult {
  previous_paid_amount: number;
  total_refunded: number;
  net_paid_amount: number;
  new_price: number;
  price_difference: number;
  requires_payment: boolean;
  requires_refund: boolean;
}

export interface DiscountDetailResult {
  code: string;
  type: string;
  amount: number;
}

export interface CalculatePriceResult {
  total_price: number;
  breakdown: PriceBreakdownResult;
  order_change: OrderChangeResult | null;
  discount_details: DiscountDetailResult[] | null;
}

// ─── Service ──────────────────────────────────────────────────────────────────

class ProductService {
  /**
   * Fetches the full product catalog, customizations, and group info.
   * @param identifier — group UUID or group code (e.g. "BIC145")
   */
  async getCatalog(identifier: string): Promise<CatalogResult | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/groups/${encodeURIComponent(identifier)}/catalog`);

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      return data || { products: [], customizations: [] };
    } catch {
      return null;
    }
  }

  /**
   * Calculates the price for a product selection, with optional discount codes
   * and change-order support.
   * @param identifier — group UUID or group code
   */
  async calculatePrice(
    identifier: string,
    request: CalculatePriceRequest
  ): Promise<CalculatePriceResult | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/groups/${encodeURIComponent(identifier)}/calculate-price`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        return null;
      }

      return await response.json();
    } catch {
      return null;
    }
  }

}

export const productService = new ProductService();
